import React, { useEffect, useRef, useState, FC } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { KSpan, KTitleSpan } from "kahuna-base-react-components"
import AdminUserAdvance from "./user-advance/AdminUserAdvance"
import AdminPortfolioOverview from "./portfolio-overview/AdminPortfolioOverview"
import AdminProfitAndLoss from "./profit-and-loss/AdminProfitAndLoss"
import AdminPerformance from "./performance/AdminPerformance"
import { connect } from "react-redux"
import { RootState } from "../../../../store"
import { checkAdminPermission } from "../../../../utility"
import { ADMIN_PERMISSIONS } from "../../../../constants"
import AdminKahunaEconomy from "./kahuna-economy/AdminKahunaEconomy"

enum TabOptions {
  PERFORMANCE = "Performance",
  USER_ADVANCE = "User Advance",
  PORTFOLIO_OVERVIEW = "Portfolio Overview",
  PROFIT_AND_LOSS = "P&L (Profit & Loss Overview)",
  KAHUNA_ECONOMY = "Kahuna Economy"
}

type AdminFinanceReportingProps = {
  userAdminPermissions?: string
}

const AdminFinanceReporting: FC<AdminFinanceReportingProps> = (props) => {
  const navigate = useNavigate()
  // /admin/finance-reporting/<deal-id>: the deal detail page belongs to the Portfolio Overview tab.
  const dealId = useParams()["*"]
  // Reloading on a deal starts with the tab sidebar closed, to give the detail page the room.
  const [openSidebar, setOpenSidebar] = useState<boolean>(!dealId)
  const [selectedTab, setSelectedTab] = useState<any>()

  useEffect(() => {
    if (dealId) setSelectedTab(TabOptions.PORTFOLIO_OVERVIEW)
  }, [dealId])

  // Opening or leaving a deal swaps the page content in place, so the scroll position of the
  // list would carry over. Reset every scrolling ancestor (the layout scrolls inside a container,
  // not necessarily the window) so the new page starts at its top.
  const contentRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    window.scrollTo(0, 0)
    for (let el = contentRef.current?.parentElement; el; el = el.parentElement) {
      if (el.scrollTop > 0) el.scrollTop = 0
    }
  }, [dealId])

  const selectTab = (tab: TabOptions) => {
    if (dealId) navigate("/admin/finance-reporting")
    setSelectedTab(tab)
  }

  const hasFinanceReportingPermission = () => {
    return (
      checkAdminPermission(ADMIN_PERMISSIONS.FINANCE_REPORTING_USER_ADVANCE, props.userAdminPermissions) ||
      checkAdminPermission(ADMIN_PERMISSIONS.FINANCE_REPORTING_PL, props.userAdminPermissions)
    )
  }

  return (
    <div className="catalog flex-grow justify-center">
      <div className="w-[100%] flex ">
        <div
          className={`${
            openSidebar ? "min-w-[230px]" : "min-w-0 !w-[0px]"
          } max-w-[320px] w-[20%] bg-[#F7F7F7] min-h-[100vh] flex flex-col overflow-hidden shrink-0`}
          style={{
            transitionDuration: "0.5s"
          }}
        >
          <div>
            <div className="py-1.5 px-4">
              <div className="p-2">
                <KTitleSpan text="Reporting" color="#111" fontSize={20} lineHeight="28px" />
              </div>

              {hasFinanceReportingPermission() && (
                <div
                  data-testid="left-bar-performance"
                  className={`rounded-[10px] p-2 flex flex-row gap-2 mt-4 items-center ${
                    selectedTab === TabOptions.PERFORMANCE ? "bg-[#FFF]" : "transparent"
                  } cursor-pointer whitespace-nowrap`}
                  onClick={() => {
                    if (selectedTab === TabOptions.PERFORMANCE) return
                    selectTab(TabOptions.PERFORMANCE)
                  }}
                >
                  <KSpan text={"Performance"} fontSize={12} lineHeight="20px" fontWeight={500} color="#111" />
                </div>
              )}

              {checkAdminPermission(ADMIN_PERMISSIONS.FINANCE_REPORTING_USER_ADVANCE, props.userAdminPermissions) && (
                <div
                  data-testid="left-bar-user-advance"
                  className={`rounded-[10px] p-2 flex flex-row gap-2 mt-1 items-center ${
                    selectedTab === TabOptions.USER_ADVANCE ? "bg-[#FFF]" : "transparent"
                  } cursor-pointer whitespace-nowrap`}
                  onClick={() => {
                    if (selectedTab === TabOptions.USER_ADVANCE) return
                    selectTab(TabOptions.USER_ADVANCE)
                  }}
                >
                  <KSpan text={"User Advance"} fontSize={12} lineHeight="20px" fontWeight={500} color="#111" />
                </div>
              )}

              {checkAdminPermission(ADMIN_PERMISSIONS.FINANCE_REPORTING_USER_ADVANCE, props.userAdminPermissions) && (
                <div
                  data-testid="left-bar-portfolio-overview"
                  className={`rounded-[10px] p-2 flex flex-row gap-2 mt-1 items-center ${
                    selectedTab === TabOptions.PORTFOLIO_OVERVIEW ? "bg-[#FFF]" : "transparent"
                  } cursor-pointer whitespace-nowrap`}
                  onClick={() => {
                    if (selectedTab === TabOptions.PORTFOLIO_OVERVIEW && !dealId) return
                    selectTab(TabOptions.PORTFOLIO_OVERVIEW)
                  }}
                >
                  <KSpan text={"Portfolio Overview"} fontSize={12} lineHeight="20px" fontWeight={500} color="#111" />
                </div>
              )}

              {checkAdminPermission(ADMIN_PERMISSIONS.FINANCE_REPORTING_PL, props.userAdminPermissions) && (
                <div
                  data-testid="left-bar-profit-and-loss"
                  className={`rounded-[10px] p-2 flex flex-row gap-1 mt-1 items-center ${
                    selectedTab === TabOptions.PROFIT_AND_LOSS ? "bg-[#FFF]" : "transparent"
                  } cursor-pointer whitespace-nowrap`}
                  onClick={() => {
                    if (selectedTab === TabOptions.PROFIT_AND_LOSS) return
                    selectTab(TabOptions.PROFIT_AND_LOSS)
                  }}
                >
                  <KSpan
                    text={"P&L (Profit & Loss Overview)"}
                    fontSize={12}
                    lineHeight="20px"
                    fontWeight={500}
                    color="#111"
                  />
                </div>
              )}

              {hasFinanceReportingPermission() && (
                <div
                  data-testid="left-bar-kahuna-economy"
                  className={`rounded-[10px] p-2 flex flex-row gap-1 mt-1 items-center ${
                    selectedTab === TabOptions.KAHUNA_ECONOMY ? "bg-[#FFF]" : "transparent"
                  } cursor-pointer whitespace-nowrap`}
                  onClick={() => {
                    if (selectedTab === TabOptions.KAHUNA_ECONOMY) return
                    selectTab(TabOptions.KAHUNA_ECONOMY)
                  }}
                >
                  <KSpan text={"Kahuna Economy"} fontSize={12} lineHeight="20px" fontWeight={500} color="#111" />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* min-w-0 is what stops the whole page from overflowing: without it this flex item
            keeps its automatic min-width, so a wide table (the portfolio deal tables run to
            ~17 nowrap columns) widens the content pane past the viewport instead of scrolling
            inside its own overflow-x container. */}
        <div ref={contentRef} className="grow min-h-0 min-w-0">
          {selectedTab === TabOptions.PERFORMANCE && (
            <AdminPerformance openSidebar={openSidebar} setOpenSidebar={setOpenSidebar} />
          )}
          {selectedTab === TabOptions.USER_ADVANCE && (
            <AdminUserAdvance openSidebar={openSidebar} setOpenSidebar={setOpenSidebar} />
          )}
          {selectedTab === TabOptions.PORTFOLIO_OVERVIEW && (
            <AdminPortfolioOverview openSidebar={openSidebar} setOpenSidebar={setOpenSidebar} />
          )}
          {selectedTab === TabOptions.PROFIT_AND_LOSS && (
            <AdminProfitAndLoss openSidebar={openSidebar} setOpenSidebar={setOpenSidebar} />
          )}
          {selectedTab === TabOptions.KAHUNA_ECONOMY && (
            <AdminKahunaEconomy openSidebar={openSidebar} setOpenSidebar={setOpenSidebar} />
          )}
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    userAdminPermissions: state.auth.user.adminPermissions
  }
}

export default connect(mapStateToProps, {})(AdminFinanceReporting)
